/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { BulkUpdateResponse } from '../models/BulkUpdateResponse';
import type { EmployeeAddress } from '../models/EmployeeAddress';
import type { EmployeeAddressRequest } from '../models/EmployeeAddressRequest';
import type { EmployeeAnniversary } from '../models/EmployeeAnniversary';
import type { EmployeeAppSettings } from '../models/EmployeeAppSettings';
import type { EmployeeAppSettingsRequest } from '../models/EmployeeAppSettingsRequest';
import type { EmployeeBenefitBudget } from '../models/EmployeeBenefitBudget';
import type { EmployeeBenefitSettings } from '../models/EmployeeBenefitSettings';
import type { EmployeeBenefitSettingsRequest } from '../models/EmployeeBenefitSettingsRequest';
import type { EmployeeBulkBenefitBudgetUpdateRequest } from '../models/EmployeeBulkBenefitBudgetUpdateRequest';
import type { EmployeeBulkEditRequest } from '../models/EmployeeBulkEditRequest';
import type { EmployeeBulkInvitePreviewResponse } from '../models/EmployeeBulkInvitePreviewResponse';
import type { EmployeeBulkInviteRequest } from '../models/EmployeeBulkInviteRequest';
import type { EmployeeContactData } from '../models/EmployeeContactData';
import type { EmployeeContactDataRequest } from '../models/EmployeeContactDataRequest';
import type { EmployeeExtended } from '../models/EmployeeExtended';
import type { EmployeeExtendedRequest } from '../models/EmployeeExtendedRequest';
import type { EmployeeGeneralData } from '../models/EmployeeGeneralData';
import type { EmployeeGeneralDataRequest } from '../models/EmployeeGeneralDataRequest';
import type { EmployeeGroup } from '../models/EmployeeGroup';
import type { EmployeeGroupUpdateRequest } from '../models/EmployeeGroupUpdateRequest';
import type { EmployeePositionData } from '../models/EmployeePositionData';
import type { EmployeePositionDataRequest } from '../models/EmployeePositionDataRequest';
import type { EmployeeTaxData } from '../models/EmployeeTaxData';
import type { EmployeeTaxDataRequest } from '../models/EmployeeTaxDataRequest';
import type { PaginatedBonusComponentList } from '../models/PaginatedBonusComponentList';
import type { PaginatedEmployeeBAVContractList } from '../models/PaginatedEmployeeBAVContractList';
import type { PaginatedEmployeeBenefitBudgetUpdatesList } from '../models/PaginatedEmployeeBenefitBudgetUpdatesList';
import type { PaginatedEmployeeJobbikeContractList } from '../models/PaginatedEmployeeJobbikeContractList';
import type { PaginatedEmployeeListItemList } from '../models/PaginatedEmployeeListItemList';
import type { PaginatedEmployeeMPPContractList } from '../models/PaginatedEmployeeMPPContractList';
import type { PaginatedUserDocumentList } from '../models/PaginatedUserDocumentList';
import type { PasswordResetTicket } from '../models/PasswordResetTicket';
import type { PatchedAcknowledgeBonusRequest } from '../models/PatchedAcknowledgeBonusRequest';
import type { PatchedEmployeeAddressRequest } from '../models/PatchedEmployeeAddressRequest';
import type { PatchedEmployeeAppSettingsRequest } from '../models/PatchedEmployeeAppSettingsRequest';
import type { PatchedEmployeeBenefitSettingsRequest } from '../models/PatchedEmployeeBenefitSettingsRequest';
import type { PatchedEmployeeBulkGroupRequest } from '../models/PatchedEmployeeBulkGroupRequest';
import type { PatchedEmployeeContactDataRequest } from '../models/PatchedEmployeeContactDataRequest';
import type { PatchedEmployeeExtendedRequest } from '../models/PatchedEmployeeExtendedRequest';
import type { PatchedEmployeeGeneralDataRequest } from '../models/PatchedEmployeeGeneralDataRequest';
import type { PatchedEmployeePositionDataRequest } from '../models/PatchedEmployeePositionDataRequest';
import type { PatchedEmployeeTaxDataRequest } from '../models/PatchedEmployeeTaxDataRequest';
import type { PatchedUserDocumentRequest } from '../models/PatchedUserDocumentRequest';
import type { SetNewPassword } from '../models/SetNewPassword';
import type { SetNewPasswordRequest } from '../models/SetNewPasswordRequest';
import type { SetUscStatus } from '../models/SetUscStatus';
import type { SetUscStatusRequest } from '../models/SetUscStatusRequest';
import type { UserDocumentRequest } from '../models/UserDocumentRequest';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class EmployeesService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * API endpoint to view and edit employees
     * @returns PaginatedEmployeeListItemList
     * @throws ApiError
     */
    public employeesList({
        company,
        costCenter,
        department,
        employeeNumber,
        groups,
        hasMissingFields,
        limit,
        location,
        offset,
        ordering,
        search,
        selectFields,
        status,
    }: {
        company: string,
        costCenter?: string,
        department?: string,
        employeeNumber?: string,
        groups?: string,
        hasMissingFields?: boolean,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        location?: string,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
        /**
         * Which field to use when ordering the results.
         */
        ordering?: string,
        /**
         * A search term.
         */
        search?: string,
        selectFields?: Array<'active_benefits_count' | 'available_benefit_budget' | 'available_benefit_budget_next_month' | 'benefits' | 'birth_date' | 'cost_center' | 'department' | 'employee_number' | 'entry_date' | 'exit_date' | 'first_name' | 'has_missing_fields' | 'has_work_email' | 'id' | 'last_name' | 'location' | 'member_groups' | 'next_month_benefit_set' | 'note' | 'position_name' | 'profile_picture' | 'remaining_benefit_budget_next_month' | 'status' | 'user'>,
        /**
         * * `INACTIVE` - INACTIVE
         * * `ACTIVE` - ACTIVE
         * * `PAUSED` - PAUSED
         * * `RESIGNED` - RESIGNED
         * * `INVITATION_PENDING_EMAIL` - INVITATION_PENDING_EMAIL
         * * `INVITATION_PENDING_PDF` - INVITATION_PENDING_PDF
         * * `DEACTIVATION_DELAYED` - DEACTIVATION_DELAYED
         */
        status?: 'ACTIVE' | 'DEACTIVATION_DELAYED' | 'INACTIVE' | 'INVITATION_PENDING_EMAIL' | 'INVITATION_PENDING_PDF' | 'PAUSED' | 'RESIGNED',
    }): CancelablePromise<PaginatedEmployeeListItemList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/employees/',
            query: {
                'company': company,
                'cost_center': costCenter,
                'department': department,
                'employee_number': employeeNumber,
                'groups': groups,
                'has_missing_fields': hasMissingFields,
                'limit': limit,
                'location': location,
                'offset': offset,
                'ordering': ordering,
                'search': search,
                'select_fields': selectFields,
                'status': status,
            },
        });
    }

    /**
     * API endpoint to view and edit employees
     * @returns EmployeeExtended
     * @throws ApiError
     */
    public employeesCreate({
        requestBody,
    }: {
        requestBody: EmployeeExtendedRequest,
    }): CancelablePromise<EmployeeExtended> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/employees/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns EmployeeAddress
     * @throws ApiError
     */
    public employeesAddressRetrieve({
        id,
    }: {
        id: string,
    }): CancelablePromise<EmployeeAddress> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/employees/{id}/address',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @returns EmployeeAddress
     * @throws ApiError
     */
    public employeesAddressUpdate({
        id,
        requestBody,
    }: {
        id: string,
        requestBody?: EmployeeAddressRequest,
    }): CancelablePromise<EmployeeAddress> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/employees/{id}/address',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns EmployeeAddress
     * @throws ApiError
     */
    public employeesAddressPartialUpdate({
        id,
        requestBody,
    }: {
        id: string,
        requestBody?: PatchedEmployeeAddressRequest,
    }): CancelablePromise<EmployeeAddress> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/employees/{id}/address',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns EmployeeAppSettings
     * @throws ApiError
     */
    public employeesAppSettingsRetrieve({
        id,
    }: {
        id: string,
    }): CancelablePromise<EmployeeAppSettings> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/employees/{id}/app-settings',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @returns EmployeeAppSettings
     * @throws ApiError
     */
    public employeesAppSettingsUpdate({
        id,
        requestBody,
    }: {
        id: string,
        requestBody?: EmployeeAppSettingsRequest,
    }): CancelablePromise<EmployeeAppSettings> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/employees/{id}/app-settings',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns EmployeeAppSettings
     * @throws ApiError
     */
    public employeesAppSettingsPartialUpdate({
        id,
        requestBody,
    }: {
        id: string,
        requestBody?: PatchedEmployeeAppSettingsRequest,
    }): CancelablePromise<EmployeeAppSettings> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/employees/{id}/app-settings',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @deprecated
     * @returns EmployeeBenefitBudget
     * @throws ApiError
     */
    public employeesBenefitBudgetRetrieve({
        accountingPeriodMonth,
        accountingPeriodYear,
        id,
    }: {
        accountingPeriodMonth: number,
        accountingPeriodYear: number,
        id: string,
    }): CancelablePromise<EmployeeBenefitBudget> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/employees/{id}/benefit-budget',
            path: {
                'id': id,
            },
            query: {
                'accounting_period_month': accountingPeriodMonth,
                'accounting_period_year': accountingPeriodYear,
            },
        });
    }

    /**
     * @returns PaginatedEmployeeBenefitBudgetUpdatesList
     * @throws ApiError
     */
    public employeesBenefitBudgetUpdatesList({
        accountingPeriodMonth,
        accountingPeriodYear,
        id,
        limit,
        offset,
    }: {
        accountingPeriodMonth: number,
        accountingPeriodYear: number,
        id: string,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
    }): CancelablePromise<PaginatedEmployeeBenefitBudgetUpdatesList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/employees/{id}/benefit-budget-updates',
            path: {
                'id': id,
            },
            query: {
                'accounting_period_month': accountingPeriodMonth,
                'accounting_period_year': accountingPeriodYear,
                'limit': limit,
                'offset': offset,
            },
        });
    }

    /**
     * @returns EmployeeBenefitSettings
     * @throws ApiError
     */
    public employeesBenefitSettingsRetrieve({
        id,
    }: {
        id: string,
    }): CancelablePromise<EmployeeBenefitSettings> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/employees/{id}/benefit-settings',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @returns EmployeeBenefitSettings
     * @throws ApiError
     */
    public employeesBenefitSettingsUpdate({
        id,
        requestBody,
    }: {
        id: string,
        requestBody: EmployeeBenefitSettingsRequest,
    }): CancelablePromise<EmployeeBenefitSettings> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/employees/{id}/benefit-settings',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns EmployeeBenefitSettings
     * @throws ApiError
     */
    public employeesBenefitSettingsPartialUpdate({
        id,
        requestBody,
    }: {
        id: string,
        requestBody?: PatchedEmployeeBenefitSettingsRequest,
    }): CancelablePromise<EmployeeBenefitSettings> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/employees/{id}/benefit-settings',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns EmployeeContactData
     * @throws ApiError
     */
    public employeesContactDataRetrieve({
        id,
    }: {
        id: string,
    }): CancelablePromise<EmployeeContactData> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/employees/{id}/contact-data',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @returns EmployeeContactData
     * @throws ApiError
     */
    public employeesContactDataUpdate({
        id,
        requestBody,
    }: {
        id: string,
        requestBody?: EmployeeContactDataRequest,
    }): CancelablePromise<EmployeeContactData> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/employees/{id}/contact-data',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns EmployeeContactData
     * @throws ApiError
     */
    public employeesContactDataPartialUpdate({
        id,
        requestBody,
    }: {
        id: string,
        requestBody?: PatchedEmployeeContactDataRequest,
    }): CancelablePromise<EmployeeContactData> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/employees/{id}/contact-data',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns PaginatedUserDocumentList
     * @throws ApiError
     */
    public employeesDocumentsList({
        id,
        limit,
        offset,
        type,
    }: {
        id: string,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
        type?: 'OTHER' | 'PAYROLL' | 'SV' | 'WAGE_TAX',
    }): CancelablePromise<PaginatedUserDocumentList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/employees/{id}/documents',
            path: {
                'id': id,
            },
            query: {
                'limit': limit,
                'offset': offset,
                'type': type,
            },
        });
    }

    /**
     * @returns PaginatedUserDocumentList
     * @throws ApiError
     */
    public employeesDocumentsUpdate({
        id,
        requestBody,
        limit,
        offset,
        type,
    }: {
        id: string,
        requestBody: UserDocumentRequest,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
        type?: 'OTHER' | 'PAYROLL' | 'SV' | 'WAGE_TAX',
    }): CancelablePromise<PaginatedUserDocumentList> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/employees/{id}/documents',
            path: {
                'id': id,
            },
            query: {
                'limit': limit,
                'offset': offset,
                'type': type,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns PaginatedUserDocumentList
     * @throws ApiError
     */
    public employeesDocumentsPartialUpdate({
        id,
        limit,
        offset,
        type,
        requestBody,
    }: {
        id: string,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
        type?: 'OTHER' | 'PAYROLL' | 'SV' | 'WAGE_TAX',
        requestBody?: PatchedUserDocumentRequest,
    }): CancelablePromise<PaginatedUserDocumentList> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/employees/{id}/documents',
            path: {
                'id': id,
            },
            query: {
                'limit': limit,
                'offset': offset,
                'type': type,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns EmployeeGeneralData
     * @throws ApiError
     */
    public employeesGeneralDataRetrieve({
        id,
    }: {
        id: string,
    }): CancelablePromise<EmployeeGeneralData> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/employees/{id}/general-data',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @returns EmployeeGeneralData
     * @throws ApiError
     */
    public employeesGeneralDataUpdate({
        id,
        requestBody,
    }: {
        id: string,
        requestBody?: EmployeeGeneralDataRequest,
    }): CancelablePromise<EmployeeGeneralData> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/employees/{id}/general-data',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns EmployeeGeneralData
     * @throws ApiError
     */
    public employeesGeneralDataPartialUpdate({
        id,
        requestBody,
    }: {
        id: string,
        requestBody?: PatchedEmployeeGeneralDataRequest,
    }): CancelablePromise<EmployeeGeneralData> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/employees/{id}/general-data',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns EmployeePositionData
     * @throws ApiError
     */
    public employeesPositionDataRetrieve({
        id,
    }: {
        id: string,
    }): CancelablePromise<EmployeePositionData> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/employees/{id}/position-data',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @returns EmployeePositionData
     * @throws ApiError
     */
    public employeesPositionDataUpdate({
        id,
        requestBody,
    }: {
        id: string,
        requestBody?: EmployeePositionDataRequest,
    }): CancelablePromise<EmployeePositionData> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/employees/{id}/position-data',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns EmployeePositionData
     * @throws ApiError
     */
    public employeesPositionDataPartialUpdate({
        id,
        requestBody,
    }: {
        id: string,
        requestBody?: PatchedEmployeePositionDataRequest,
    }): CancelablePromise<EmployeePositionData> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/employees/{id}/position-data',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns SetNewPassword
     * @throws ApiError
     */
    public employeesSetNewPasswordCreate({
        id,
        requestBody,
    }: {
        id: string,
        requestBody: SetNewPasswordRequest,
    }): CancelablePromise<SetNewPassword> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/employees/{id}/set-new-password',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns EmployeeTaxData
     * @throws ApiError
     */
    public employeesTaxDataRetrieve({
        id,
    }: {
        id: string,
    }): CancelablePromise<EmployeeTaxData> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/employees/{id}/tax-data',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @returns EmployeeTaxData
     * @throws ApiError
     */
    public employeesTaxDataUpdate({
        id,
        requestBody,
    }: {
        id: string,
        requestBody?: EmployeeTaxDataRequest,
    }): CancelablePromise<EmployeeTaxData> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/employees/{id}/tax-data',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns EmployeeTaxData
     * @throws ApiError
     */
    public employeesTaxDataPartialUpdate({
        id,
        requestBody,
    }: {
        id: string,
        requestBody?: PatchedEmployeeTaxDataRequest,
    }): CancelablePromise<EmployeeTaxData> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/employees/{id}/tax-data',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns PaginatedEmployeeBAVContractList
     * @throws ApiError
     */
    public employeesBenefitSettingsBavContractsList({
        parentEmployee,
        limit,
        offset,
    }: {
        /**
         * A UUID string identifying this employee bav contract.
         */
        parentEmployee: string,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
    }): CancelablePromise<PaginatedEmployeeBAVContractList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/employees/{parent_employee}/benefit-settings/bav/contracts/',
            path: {
                'parent_employee': parentEmployee,
            },
            query: {
                'limit': limit,
                'offset': offset,
            },
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns PaginatedEmployeeJobbikeContractList
     * @throws ApiError
     */
    public employeesBenefitSettingsJobbikeContractsList({
        parentEmployee,
        limit,
        offset,
    }: {
        /**
         * A UUID string identifying this employee jobbike contract.
         */
        parentEmployee: string,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
    }): CancelablePromise<PaginatedEmployeeJobbikeContractList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/employees/{parent_employee}/benefit-settings/jobbike/contracts/',
            path: {
                'parent_employee': parentEmployee,
            },
            query: {
                'limit': limit,
                'offset': offset,
            },
        });
    }

    /**
     * Permission Viewset mixin to override the default
     * behavior from AutoPermissionViewSetMixin.
     * @returns PaginatedEmployeeMPPContractList
     * @throws ApiError
     */
    public employeesBenefitSettingsMppContractsList({
        parentEmployee,
        limit,
        offset,
    }: {
        /**
         * A UUID string identifying this employee mpp contract.
         */
        parentEmployee: string,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
    }): CancelablePromise<PaginatedEmployeeMPPContractList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/employees/{parent_employee}/benefit-settings/mpp/contracts/',
            path: {
                'parent_employee': parentEmployee,
            },
            query: {
                'limit': limit,
                'offset': offset,
            },
        });
    }

    /**
     * @returns SetUscStatus
     * @throws ApiError
     */
    public employeesBenefitSettingsSachbezugSetUscStatusUpdate({
        parentEmployee,
        requestBody,
    }: {
        /**
         * A UUID string identifying this employee benefit settings.
         */
        parentEmployee: string,
        requestBody: SetUscStatusRequest,
    }): CancelablePromise<SetUscStatus> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/employees/{parent_employee}/benefit-settings/sachbezug/set_usc_status/',
            path: {
                'parent_employee': parentEmployee,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns PasswordResetTicket
     * @throws ApiError
     */
    public employeesPasswordResetTicketsRetrieve({
        id,
        parentEmployee,
    }: {
        /**
         * A UUID string identifying this Password Reset.
         */
        id: string,
        parentEmployee: string,
    }): CancelablePromise<PasswordResetTicket> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/employees/{parent_employee}/password-reset-tickets/{id}/',
            path: {
                'id': id,
                'parent_employee': parentEmployee,
            },
        });
    }

    /**
     * API endpoint to view and edit employees
     * @returns EmployeeExtended
     * @throws ApiError
     */
    public employeesRetrieve({
        id,
    }: {
        id: string,
    }): CancelablePromise<EmployeeExtended> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/employees/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * API endpoint to view and edit employees
     * @returns EmployeeExtended
     * @throws ApiError
     */
    public employeesUpdate({
        id,
        requestBody,
    }: {
        /**
         * A UUID string identifying this employee.
         */
        id: string,
        requestBody: EmployeeExtendedRequest,
    }): CancelablePromise<EmployeeExtended> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/employees/{id}/',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * API endpoint to view and edit employees
     * @returns EmployeeExtended
     * @throws ApiError
     */
    public employeesPartialUpdate({
        id,
        requestBody,
    }: {
        /**
         * A UUID string identifying this employee.
         */
        id: string,
        requestBody?: PatchedEmployeeExtendedRequest,
    }): CancelablePromise<EmployeeExtended> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/employees/{id}/',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * API endpoint to view and edit employees
     * @returns void
     * @throws ApiError
     */
    public employeesDestroy({
        id,
    }: {
        /**
         * A UUID string identifying this employee.
         */
        id: string,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/employees/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * API endpoint to view and edit employees
     * @returns binary
     * @throws ApiError
     */
    public employeesInvitationPdfCreate({
        id,
    }: {
        id: string,
    }): CancelablePromise<Blob> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/employees/{id}/invitation-pdf/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * API endpoint to view and edit employees
     * @returns any No response body
     * @throws ApiError
     */
    public employeesSendBavAppointmentEmailCreate({
        id,
    }: {
        id: string,
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/employees/{id}/send-bav-appointment-email/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * API endpoint to view and edit employees
     * @returns any No response body
     * @throws ApiError
     */
    public employeesSendInvitationEmailCreate({
        id,
    }: {
        id: string,
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/employees/{id}/send-invitation-email/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * API endpoint to view and edit employees
     * @returns EmployeeGroup
     * @throws ApiError
     */
    public employeesUpdateGroupsUpdate({
        id,
        requestBody,
    }: {
        /**
         * A UUID string identifying this employee.
         */
        id: string,
        requestBody: EmployeeGroupUpdateRequest,
    }): CancelablePromise<EmployeeGroup> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/employees/{id}/update-groups/',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * API endpoint to view and edit employees
     * @returns EmployeeExtended
     * @throws ApiError
     */
    public employeesAcknowledgeBonusPartialUpdate({
        requestBody,
    }: {
        requestBody?: PatchedAcknowledgeBonusRequest,
    }): CancelablePromise<EmployeeExtended> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/employees/acknowledge-bonus/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * API endpoint to view and edit employees
     * @returns PaginatedBonusComponentList
     * @throws ApiError
     */
    public employeesBonusesList({
        limit,
        offset,
    }: {
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
    }): CancelablePromise<PaginatedBonusComponentList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/employees/bonuses/',
            query: {
                'limit': limit,
                'offset': offset,
            },
        });
    }

    /**
     * API endpoint to view and edit employees
     * @returns EmployeeExtended
     * @throws ApiError
     */
    public employeesBulkCreateOrUpdateEmployeesCreate({
        requestBody,
    }: {
        requestBody: EmployeeExtendedRequest,
    }): CancelablePromise<EmployeeExtended> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/employees/bulk-create-or-update-employees/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * API endpoint to view and edit employees
     * @returns BulkUpdateResponse
     * @throws ApiError
     */
    public employeesBulkEditUpdate({
        requestBody,
    }: {
        requestBody: EmployeeBulkEditRequest,
    }): CancelablePromise<BulkUpdateResponse> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/employees/bulk-edit/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * API endpoint to view and edit employees
     * @returns binary
     * @throws ApiError
     */
    public employeesBulkInviteCreate({
        requestBody,
    }: {
        requestBody: EmployeeBulkInviteRequest,
    }): CancelablePromise<Blob> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/employees/bulk-invite/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * API endpoint to view and edit employees
     * @returns EmployeeBulkInvitePreviewResponse
     * @throws ApiError
     */
    public employeesBulkInvitePreviewCreate({
        requestBody,
    }: {
        requestBody: EmployeeBulkInviteRequest,
    }): CancelablePromise<EmployeeBulkInvitePreviewResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/employees/bulk-invite-preview/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * API endpoint to view and edit employees
     * @returns EmployeeGroup
     * @throws ApiError
     */
    public employeesBulkUpdateGroupsPartialUpdate({
        requestBody,
    }: {
        requestBody?: PatchedEmployeeBulkGroupRequest,
    }): CancelablePromise<EmployeeGroup> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/employees/bulk-update-groups/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * API endpoint to view and edit employees
     * @returns PaginatedBonusComponentList
     * @throws ApiError
     */
    public employeesUnacknowledgedBonusesList({
        limit,
        offset,
    }: {
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
    }): CancelablePromise<PaginatedBonusComponentList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/employees/unacknowledged-bonuses/',
            query: {
                'limit': limit,
                'offset': offset,
            },
        });
    }

    /**
     * API endpoint to view and edit employees
     * @returns EmployeeAnniversary
     * @throws ApiError
     */
    public employeesUpcomingAnniversariesRetrieve(): CancelablePromise<EmployeeAnniversary> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/employees/upcoming-anniversaries/',
        });
    }

    /**
     * API endpoint to view and edit employees
     * @returns BulkUpdateResponse
     * @throws ApiError
     */
    public employeesUpdateBenefitBudgetsUpdate({
        requestBody,
        company,
    }: {
        requestBody: EmployeeBulkBenefitBudgetUpdateRequest,
        company?: string,
    }): CancelablePromise<BulkUpdateResponse> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/employees/update-benefit-budgets/',
            query: {
                'company': company,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

}
